// ---------------------------------------------------------------------------
// 日付の欄（components/DateInput.tsx、受け取った書類 S3＝../eigyouryoku/docs/13 §4.5・★10）。
//   文字で打った日付を `YYYY/MM/DD` に直す（typeZip・typePhone と同じ作法＝打った文字から欄に見せる形を作る）。
//   受けるのは 全角・8桁（20261009）・区切り（/ - . 年月日）・和暦（M・T・S・H・R と 明治・大正・昭和・平成・令和、元年も）・
//   年2桁（#320：26/11/05・261105＝2000年代。区切りは / - . だけ）。欄に見せる形は年2桁（dateShortOf＝26/11/05）。
//   値の受け渡し（保存・API）はいつも `YYYY-MM-DD`（今の type="date" の値と同じ）。読めなければ null＝欄は打った文字のまま残して朱の1行
// ---------------------------------------------------------------------------

/** 元号の初年の前の年（令和1年＝2019 → 2018）。和暦の年＋これ＝西暦 */
const ERA_BASE: Record<string, number> = { M: 1867, T: 1911, S: 1925, H: 1988, R: 2018, 明治: 1867, 大正: 1911, 昭和: 1925, 平成: 1988, 令和: 2018 };
/** 欄で受ける年の範囲（明治元年〜） */
const YEAR_MIN = 1868;
const YEAR_MAX = 2200;

const pad = (n: number) => String(n).padStart(2, "0");

/** 正しい暦の日なら [年, 月, 日]、違えば null（2月30日・13月・うるう年でない2月29日は null） */
function validDay(y: number, m: number, d: number): [number, number, number] | null {
  if (!Number.isInteger(y) || !Number.isInteger(m) || !Number.isInteger(d)) return null;
  if (y < YEAR_MIN || y > YEAR_MAX || m < 1 || m > 12 || d < 1 || d > 31) return null;
  const dt = new Date(Date.UTC(y, m - 1, d));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== m - 1 || dt.getUTCDate() !== d) return null;
  return [y, m, d];
}

/**
 * 打った日付 → `YYYY/MM/DD`。空（空白だけ）は ""。読めなければ null。
 *   「２０２６／１０／０９」「20261009」「2026-10-9」「2026.10.9」「2026年10月9日」→「2026/10/09」
 *   「R8.10.9」「令和8年10月9日」→「2026/10/09」、「H30/1/5」→「2018/01/05」、「S64.1.7」→「1989/01/07」、「令和元年5月1日」→「2019/05/01」
 *   「2026/13/40」「2026/2/29」は null（暦に無い日）
 */
export function typeDate(raw: string): string | null {
  const t = raw.normalize("NFKC").replace(/\s+/g, "").replace(/[‐－−ー―]/g, "-");
  if (!t) return "";
  let ymd: [number, number, number] | null = null;
  const era = /^(明治|大正|昭和|平成|令和|[MTSHR])(元|\d{1,2})[年.\/-](\d{1,2})[月.\/-](\d{1,2})日?$/i.exec(t);
  const west = /^(\d{4})[年.\/-](\d{1,2})[月.\/-](\d{1,2})日?$/.exec(t);
  const digits = /^(\d{4})(\d{2})(\d{2})$/.exec(t);
  // 年2桁（#320）：「26/11/05」「26-1-5」「261105」は 2000年代（2026/11/05）。区切りは / - . だけ（「30年1月5日」は平成30年と取り違えるので読まない＝和暦は頭に元号が要る）
  const west2 = /^(\d{2})[.\/-](\d{1,2})[.\/-](\d{1,2})$/.exec(t);
  const digits2 = /^(\d{2})(\d{2})(\d{2})$/.exec(t);
  if (era) {
    const base = ERA_BASE[era[1]!.length === 1 ? era[1]!.toUpperCase() : era[1]!];
    if (base == null) return null;
    const n = era[2] === "元" ? 1 : Number(era[2]);
    if (n < 1) return null;
    ymd = validDay(base + n, Number(era[3]), Number(era[4]));
  } else if (west) {
    ymd = validDay(Number(west[1]), Number(west[2]), Number(west[3]));
  } else if (digits) {
    ymd = validDay(Number(digits[1]), Number(digits[2]), Number(digits[3]));
  } else if (west2) {
    ymd = validDay(2000 + Number(west2[1]), Number(west2[2]), Number(west2[3]));
  } else if (digits2) {
    ymd = validDay(2000 + Number(digits2[1]), Number(digits2[2]), Number(digits2[3]));
  }
  return ymd ? `${ymd[0]}/${pad(ymd[1])}/${pad(ymd[2])}` : null;
}

/** 欄に見せる形 → 値（`2026/10/09` → `2026-10-09`）。typeDate の答えを渡す */
export const dateValueOf = (shown: string): string => shown.replace(/\//g, "-");

/** 値（`2026-10-09`）→ 欄に見せる形（`2026/10/09`）。空・形の違う値はそのまま */
export const dateShownOf = (value: string | null | undefined): string => (value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value.replace(/-/g, "/") : (value ?? ""));
/**
 * 値（`2026-10-09`）か typeDate の答え（`2026/10/09`）→ 画面の形（`26/10/09`、#320 ★8＝日付の欄も年2桁で見せる）。
 * 2000〜2099年だけ年2桁（ほかの年＝和暦の昭和・平成の古い日などは取り違えないよう4桁のまま）。空・形の違う値はそのまま
 */
export const dateShortOf = (value: string | null | undefined): string => {
  const m = value ? /^(\d{4})[-/](\d{2})[-/](\d{2})$/.exec(value) : null;
  if (!m) return value ?? "";
  return m[1]!.startsWith("20") ? `${m[1]!.slice(2)}/${m[2]}/${m[3]}` : `${m[1]}/${m[2]}/${m[3]}`;
};

/** 日付の欄の文（DateInput） */
export const DATE_INPUT_MSG = {
  /** 読めなかったとき（欄の下の朱の1行） */
  invalid: "日付になっていません",
  /** カレンダーのアイコンの読み上げ（「見積日をカレンダーから選択」） */
  pick: (label: string) => `${label}をカレンダーから選択`,
  today: "今日",
  clear: "日付削除",
  prevMonth: "前の月",
  nextMonth: "次の月",
} as const;
